'use client';

import { useState, useEffect, useRef } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';
import { logger } from "@/lib/logger";
import { login, verify2FA, sendLoginCode } from '@/lib/auth';

export type LoginFormData = {
    email: string;
    password: string;
};

export type Step = 'credentials' | 'otp';

export function useLoginForm() {
    const t = useTranslations('auth');
    const tSecurity = useTranslations('settings.security');
    const router = useRouter();
    const searchParams = useSearchParams();
    // Only honor a redirect that looks like a real in-app path. Reject anything that
    // doesn't start with "/", contains "//" or "\" (open-redirect bait), or decodes to
    // just "/" (which router.push would treat as a literal "%2F" segment → 404).
    const rawRedirect = searchParams.get('redirect');
    const redirectUrl = (() => {
        if (!rawRedirect) return '/';
        if (!rawRedirect.startsWith('/') || rawRedirect.startsWith('//') || rawRedirect.includes('\\')) return '/';
        if (rawRedirect === '/' || rawRedirect === '%2F') return '/';
        return rawRedirect;
    })();

    const [step, setStep] = useState<Step>('credentials');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const isSubmittingRef = useRef(false);
    const [otpCode, setOtpCode] = useState('');
    const [ticket, setTicket] = useState<string | null>(null);
    const [availableMethods, setAvailableMethods] = useState<string[]>([]);
    const [selectedMethod, setSelectedMethod] = useState<string>('');
    const [isSendingCode, setIsSendingCode] = useState(false);
    const [countdowns, setCountdowns] = useState<Record<string, number>>({});
    const [methodStatuses, setMethodStatuses] = useState<Record<string, string>>({});

    const form = useForm<LoginFormData>();

    const onSubmitCredentials = async (data: LoginFormData) => {
        setIsLoading(true);
        setError(null);

        try {
            const response = await login(data.email, data.password);

            if (response['2fa_required'] && response.ticket) {
                const loginTicket = response.ticket;
                setTicket(loginTicket);
                const methods = (response.two_factor_methods || 'whatsapp').split(',');
                setAvailableMethods(methods);
                setMethodStatuses(response.method_statuses || {});

                if (methods.includes('totp')) {
                    setSelectedMethod('totp');
                } else if (methods.includes('whatsapp')) {
                    setSelectedMethod('whatsapp');
                    const statuses = response.method_statuses || {};
                    if (statuses['whatsapp'] === 'connected') {
                        sendLoginCode(loginTicket, 'whatsapp').catch(err => {
                            logger.error({ error: err }, 'Failed to auto-send WhatsApp code');
                        });
                        setCountdowns(prev => ({ ...prev, whatsapp: 60 }));
                        toast.success(tSecurity('whatsappCodeSent') || 'Verification code sent via WhatsApp');
                    }
                } else if (methods.includes('telegram')) {
                    setSelectedMethod('telegram');
                    sendLoginCode(loginTicket, 'telegram').catch(err => {
                        logger.error({ error: err }, 'Failed to auto-send Telegram code');
                    });
                    setCountdowns(prev => ({ ...prev, telegram: 60 }));
                    toast.success(tSecurity('telegramCodeSent') || 'Verification code sent via Telegram');
                }

                setStep('otp');
            } else {
                router.push(redirectUrl);
                router.refresh();
            }
        } catch (err: unknown) {
            const e = err as { data?: { error?: string }; message?: string };
            const message = e?.data?.error || e?.message || t('loginFailed');
            setError(message);
        } finally {
            setIsLoading(false);
        }
    };

    const onSubmitOtp = async () => {
        if (isLoading || isSubmittingRef.current || otpCode.length < 6 || !ticket || !selectedMethod) return;

        setIsLoading(true);
        isSubmittingRef.current = true;
        setError(null);

        try {
            await verify2FA(ticket, otpCode, selectedMethod);
            router.push(redirectUrl);
            router.refresh();
        } catch (err: unknown) {
            const e = err as { data?: { error?: string }; message?: string };
            const message = e?.data?.error || e?.message || 'Invalid verification code';
            setError(message);
        } finally {
            setIsLoading(false);
            isSubmittingRef.current = false;
        }
    };

    const onResendCode = async (method?: string) => {
        const targetMethod = method || selectedMethod;
        const currentCountdown = countdowns[targetMethod] || 0;
        if (!ticket || currentCountdown > 0 || !targetMethod) return;

        setIsSendingCode(true);
        try {
            if (targetMethod === 'whatsapp') {
                await sendLoginCode(ticket, 'whatsapp');
                toast.success(t('otpResent'));
            } else if (targetMethod === 'telegram') {
                await sendLoginCode(ticket, 'telegram');
                toast.success(t('otpResent'));
            } else {
                toast.error('Cannot resend code for this method.');
            }
            setCountdowns(prev => ({ ...prev, [targetMethod]: 60 }));
        } catch (err: unknown) {
            const e = err as { data?: { error?: string }; message?: string };
            const message = e?.data?.error || e?.message || 'Failed to resend code';
            setError(message);
            toast.error(message);
        } finally {
            setIsSendingCode(false);
        }
    };

    const goBackToCredentials = () => {
        setStep('credentials');
        setOtpCode('');
        setTicket(null);
        setError(null);
    };

    useEffect(() => {
        const activeMethods = Object.keys(countdowns).filter(m => countdowns[m] > 0);
        if (activeMethods.length > 0) {
            const timer = setTimeout(() => {
                setCountdowns(prev => {
                    const next = { ...prev };
                    activeMethods.forEach(m => {
                        next[m] = Math.max(0, next[m] - 1);
                    });
                    return next;
                });
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [countdowns]);

    useEffect(() => {
        if (otpCode.length === 6 && step === 'otp') {
            // eslint-disable-next-line react-hooks/set-state-in-effect -- auto-submitting on full OTP entry; submit handler manages its own state
            onSubmitOtp();
        }
    }, [otpCode, step]);

    return {
        step,
        isLoading,
        error,
        otpCode,
        setOtpCode,
        availableMethods,
        selectedMethod,
        setSelectedMethod,
        isSendingCode,
        countdowns,
        methodStatuses,
        form,
        onSubmitCredentials,
        onSubmitOtp,
        onResendCode,
        goBackToCredentials,
        t,
        tSecurity,
    };
}
